import React, { useState } from 'react';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ListAltIcon from '@material-ui/icons/ListAlt';
import Drawer from '@material-ui/core/Drawer';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/styles';
import { NavLink } from 'react-router-dom';
import { graphql, compose } from 'react-apollo';
import _get from 'lodash/get';

import AppLogo from 'components/theme/app-logo';
import SignOut from 'components/auth/sign-out';
import SelectApplication from 'components/sidebar/select-application';
import SelectEnvironment from 'components/sidebar/select-environment';
import ListAuditLogs from 'components/sidebar/list-audit-logs';
import withGraphqlLoaderHOC from 'components/theme/loader/with-graphql-loader-hoc';
import * as routeUtils from 'utils/routes';
import gqlQueryGetAppContext from 'graphql/queries/get-app-context';
import gqlQueryGetEnvContext from 'graphql/queries/get-env-context';
import * as constants from 'app-constants';

const useStyle = makeStyles(
  (theme) => {
    // + 1 to add border applied to sidebar of 1px
    const drawerCloseWidth = theme.spacing.unit * 8 + 1;
    return {
      drawerCommon: {
        overflowX: 'hidden'
      },
      drawer: {
        width: theme.drawerOpenWidth,
        flexShrink: 0
      },
      drawerOpen: {
        width: theme.drawerOpenWidth,
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen
        })
      },
      drawerClose: {
        width: drawerCloseWidth,
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen
        }),
        '& $listItemText': {
          opacity: 0
        },
        '& $listIcon': {
          marginLeft: 5
        }
      },
      listIcon: {},
      listItemText: {
        transition: theme.transitions.create('opacity')
      },
      sidebarFooter: {
        position: 'absolute',
        bottom: 0,
        width: '100%'
      },
      sidebarToggle: {
        maxWidth: 48,
        float: 'right',
        margin: [[0, theme.spacing.unit / 2]]
      },
      paper: {
        color: theme.palette.primary.contrastText
      },
      sidebarLogo: {
        padding: [[0, 4]],
        textAlign: 'center'
      },
      list: {
        paddingTop: 0,
        paddingBottom: 0
      },
      listItem: {
        '&.selected': {
          background: 'rgba(0, 0, 0, 0.08)'
        }
      }
    };
  },
  { withTheme: true }
);

function Sidebar(props) {
  const [drawerState, setDrawerOpen] = useState(true);
  const classes = useStyle();
  const isListAuditLogsShow = constants.flags.IsListAuditLogsShow();
  const toggleDrawer = () => {
    setDrawerOpen(!drawerState);
  };

  const appContext = _get(props, 'data.queryAppcontext[0]', {});
  const envContext = _get(props, 'data.queryEnvcontext[0]', {});

  return (
    <Drawer
      variant="permanent"
      data-testid="drawer-sidebar"
      className={classNames(classes.drawer, classes.drawerCommon, {
        [classes.drawerOpen]: drawerState,
        [classes.drawerClose]: !drawerState
      })}
      classes={{
        paper: classNames(classes.drawerCommon, classes.paper, {
          [classes.drawerOpen]: drawerState,
          [classes.drawerClose]: !drawerState
        })
      }}
      open={drawerState}
    >
      <div className={classes.sidebarLogo}>
        <AppLogo size="small" collapse={!drawerState} />
      </div>

      <Divider />

      <List className={classes.list}>
        <SelectApplication
          app={appContext}
          collapse={!drawerState}
          listItemClass={classes.listItem}
          listItemTextClass={classes.listItemText}
        />
        <Divider />
        <SelectEnvironment
          app={appContext}
          env={envContext}
          collapse={!drawerState}
          listItemClass={classes.listItem}
          listItemTextClass={classes.listItemText}
        />
        <Divider />
        <ListItem
          button
          component={NavLink}
          to={{
            pathname: routeUtils.build.pathFeaturesList(
              appContext.appId,
              envContext.name
            )
          }}
          activeClassName="selected"
          className={classes.listItem}
          disabled={!appContext.appId}
          data-testid="features-link"
        >
          <ListItemIcon className={classes.listIcon}>
            <ListAltIcon />
          </ListItemIcon>
          <ListItemText
            className={classNames(classes.listItemText)}
            primary="Features"
          />
        </ListItem>
      </List>

      <div className={classes.sidebarFooter}>
        <Divider />
        <List className={classes.list}>
          {isListAuditLogsShow && (
            <>
              <ListAuditLogs
                listItemClass={classes.listItem}
                listItemTextClass={classes.listItemText}
                listIcon={classes.listIcon}
              />
              <Divider />
            </>
          )}
          <SignOut
            listItemClass={classes.listItem}
            listItemTextClass={classes.listItemText}
            listIcon={classes.listIcon}
          />
        </List>
        <Divider />
        <IconButton
          aria-label="Open or Close drawer"
          onClick={toggleDrawer}
          className={classes.sidebarToggle}
        >
          {drawerState ? (
            <ChevronLeftIcon aria-label="Close drawer" />
          ) : (
            <ChevronRightIcon aria-label="Open drawer" />
          )}
        </IconButton>
      </div>
    </Drawer>
  );
}

const SidebarWithGraphqlLoder = withGraphqlLoaderHOC(
  Sidebar,
  [
    {
      name: 'queryAppcontext',
      data: 'appContext'
    },
    {
      name: 'queryEnvcontext',
      data: 'envContext'
    }
  ],
  { showLoader: false }
);

export default compose(
  graphql(gqlQueryGetAppContext, {
    name: 'queryAppcontext'
  }),
  graphql(gqlQueryGetEnvContext, {
    name: 'queryEnvcontext'
  })
)(SidebarWithGraphqlLoder);
